// src/components/AccountSideBar/AccountSidebar.tsx
import React, { useEffect, useRef, useState } from "react";
import "./AccountSidebar.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useProfile } from "../Profile/ProfileContext";
import { isAdmin } from "../../auth/roles";
import { useOrderNotifications } from "../../contexts/OrderNotificationsContext";
import GoodbyeToast from "../core/goodbyeToast";

type Props = {
  open: boolean;
  onClose: () => void;
  onOpenAuthModal: () => void;
};

export default function AccountSidebar({
  open,
  onClose,
  onOpenAuthModal,
}: Props) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { isAuthed, logout, user } = useAuth();
  const { profile } = useProfile();
  const { actionCount: orderActionCount } = useOrderNotifications();
  const firstBtnRef = useRef<HTMLButtonElement | null>(null);

  const [showGoodbye, setShowGoodbye] = useState(false);

  useEffect(() => {
    if (open) setTimeout(() => firstBtnRef.current?.focus(), 0);
  }, [open]);

  const goTo = (path: string) => {
    onClose();
    nav(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Logout UX should still continue when session is already invalid.
    } finally {
      // Keep UX stable even if backend logout reports an already-expired session.
      setShowGoodbye(true);
      onClose();
      nav("/");
    }
  };

  // ✅ render altijd toast, zelfs als sidebar dicht is
  return (
    <>
      <GoodbyeToast
        open={showGoodbye}
        title={t("account.logoutToast")}
        message={t("account.logoutMessage")}
        onClose={() => setShowGoodbye(false)}
      />

      {/* sidebar zelf alleen tonen als open */}
      {open && (
        <div
          className={`as-overlay ${open ? "as-overlay--open" : ""}`}
          onClick={onClose}
        >
          <aside
            className={`as-panel ${open ? "as-panel--open" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="as-header">
              <div className="as-user">
                <div className="as-avatar">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={t("common.avatar")}
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "999px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <span>🙂</span>
                  )}
                </div>

                <div className="as-user-info">
                  <div className="as-user-name">
                    {isAuthed ? profile?.username : t("account.notLoggedIn")}
                  </div>
                </div>
              </div>

              <button
                className="as-close"
                type="button"
                onClick={onClose}
                aria-label={t("common.close")}
              >
                ✕
              </button>
            </div>

            <nav className="as-nav" aria-label={t("account.accountOptions")}>
              {isAuthed ? (
                <>
                  <button
                    ref={firstBtnRef}
                    type="button"
                    onClick={() => goTo("/profile")}
                  >
                    {t("account.myProfile")}
                  </button>

                  <button
                    type="button"
                    onClick={() => goTo("/orders")}
                    style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    {t("account.orders")}
                    {orderActionCount > 0 && (
                      <span
                        style={{
                          minWidth: "22px",
                          height: "22px",
                          borderRadius: "999px",
                          background: "#0B5C55",
                          color: "#fff",
                          fontSize: "11.5px",
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 6px",
                        }}
                      >
                        {orderActionCount}
                      </span>
                    )}
                  </button>

                  <button type="button" onClick={() => goTo("/settings")}>
                    {t("account.settings")}
                  </button>

                  {/* Chats hidden — kept for future use
                  <button type="button" onClick={() => goTo("/chats")}>
                    {t("account.chats")}
                  </button>
                  */}

                  {isAdmin(user?.role) && (
                    <button
                      type="button"
                      id="shirt-moderate-nav"
                      onClick={() => goTo("/admin/shirts")}
                    >
                      {t("account.shirtModeration")}
                    </button>
                  )}

                  {isAdmin(user?.role) && (
                    <button
                      type="button"
                      id="shirt-list-nav"
                      onClick={() => goTo("/admin/shirt-list")}
                    >
                      {t("account.shirtList")}
                    </button>
                  )}

                  {isAdmin(user?.role) && (
                    <button
                      type="button"
                      id="user-moderate-nav"
                      onClick={() => goTo("/admin/users")}
                    >
                      {t("account.userManagement")}
                    </button>
                  )}

                  {isAdmin(user?.role) && (
                    <button
                      type="button"
                      id="disputes-nav"
                      onClick={() => goTo("/admin/disputes")}
                    >
                      {t("account.disputes", "Disputes")}
                    </button>
                  )}
                </>
              ) : (
                <button
                  ref={firstBtnRef}
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenAuthModal();
                  }}
                >
                  {t("account.loginOrRegister")}
                </button>
              )}
            </nav>

            {isAuthed && (
              <div className="as-logout">
                <button
                  className="as-logout-btn"
                  type="button"
                  onClick={handleLogout}
                >
                  {t("account.logout")}
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
